import {
  followsStandardModel,
  latestStandardModel,
} from "./standard-model-policy";

export type RuntimeKey = "claude_cli" | "codex_cli" | "opencode_cli";

export const RUNTIME_DEFAULT_MODEL: Record<RuntimeKey, string> = {
  claude_cli: "claude-opus-5-5",
  codex_cli: "gpt-6.1-sol",
  opencode_cli: "openrouter/qwen/qwen3.8-flash",
};

// Verified against https://opencode.ai/zen/v1/models on 2026-09-07.
// Zen credentials need a Zen model; the OpenRouter default uses a separate key.
export const OPENCODE_ZEN_DEFAULT_MODEL = "opencode/qwen3.6-plus";

export const MANAGED_OPENCODE_DEFAULT_MODEL = "openrouter/qwen/qwen3.8-flash";

export function getRuntimeDefaultModel(
  runtime: RuntimeKey,
  runtimeSource?: string | null,
  models?: readonly { id: string; free?: boolean }[],
): string {
  if (runtime === "opencode_cli" && runtimeSource === "denker_starter") {
    return MANAGED_OPENCODE_DEFAULT_MODEL;
  }
  const fallback = RUNTIME_DEFAULT_MODEL[runtime];
  if (runtime !== "opencode_cli" && models?.length) {
    return latestStandardModel(runtime, models) ?? fallback;
  }
  if (runtime === "opencode_cli" && models?.length) {
    return (
      models.find((model) => model.id === fallback)?.id ??
      models.find(
        (model) =>
          model.id === OPENCODE_ZEN_DEFAULT_MODEL ||
          `opencode/${model.id}` === OPENCODE_ZEN_DEFAULT_MODEL,
      )?.id ??
      models.find((model) => model.free)?.id ??
      models[0]!.id
    );
  }
  return fallback;
}

export interface RuntimeAvailability {
  type: RuntimeKey;
  managed?: boolean;
  source?: string;
}

/** Prefer a user-installed runtime, otherwise retain the bundled runtime source. */
export function getPreferredRuntimeSource(
  runtime: RuntimeKey,
  availableRuntimes: readonly RuntimeAvailability[],
): string | null {
  if (runtime !== "opencode_cli") return null;
  const matching = availableRuntimes.filter((item) => item.type === runtime);
  if (matching.some((item) => !item.managed)) return null;
  return matching.some(
    (item) => item.managed && item.source === "denker_starter",
  )
    ? "denker_starter"
    : null;
}

export function isRuntimeKey(value: string): value is RuntimeKey {
  return (
    value === "claude_cli" || value === "codex_cli" || value === "opencode_cli"
  );
}

/** Resolve an agent template to the adapter and model execution will use. */
export function resolveTemplateModel(
  template:
    | {
        adapter_type?: string | null;
        name?: string;
        is_builtin?: boolean;
        adapter_config?: {
          model?: string;
          runtime_source?: string;
          model_policy?: string;
        } | null;
      }
    | null
    | undefined,
  availableModels: Partial<
    Record<string, readonly { id: string; free?: boolean }[]>
  > = {},
): { adapterType: RuntimeKey; model: string | undefined } {
  const rawAdapter = template?.adapter_type ?? "";
  const adapterType: RuntimeKey = isRuntimeKey(rawAdapter)
    ? rawAdapter
    : "claude_cli";
  return {
    adapterType,
    model:
      (template && followsStandardModel(template)
        ? getRuntimeDefaultModel(
            adapterType,
            template.adapter_config?.runtime_source,
            availableModels[adapterType],
          )
        : template?.adapter_config?.model) ??
      getRuntimeDefaultModel(
        adapterType,
        template?.adapter_config?.runtime_source,
      ),
  };
}
